import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import TableEmpty from '@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue';
import ITableCol from "@/interfaces/ITableCol";
import IZodiacDescriptions from "@/interfaces/IZodiacDescriptions";
import IZodiacSigns from "@/interfaces/IZodiacSigns";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import cleanData from "@/libs/cleanData";
import cloneObject from "@/libs/cloneObject";
import getParamQuery from "@/libs/getParamQuery";
import getQueryData from "@/libs/getQueryData";
import getTableColumns from "@/libs/getTableColumns";
import isEmpty from "@/libs/isEmpty";
import {transformMultiSelect} from "@/libs/transformData/transformMultiSelect";
import searchByTable from "@/libs/searchQuery/searchByTable";
import Multiselect from 'vue-multiselect';
import {Component, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';

import DefaultTools from '@/components/Includes/DefaultTools/DefaultTools.vue';
import Loader from '@/components/Includes/Loader/Loader.vue';
import Pagination from '@/components/Includes/Pagination/Pagination.vue';
import IPayloadQuery from '@/interfaces/IPayloadQuery';
import {Switch as cSwitch} from '@coreui/vue';

import ZodiacDescriptionsCreate from '@/components/Astrology/ZodiacDescriptions/ZodiacDescriptionsCreate/ZodiacDescriptionsCreate.vue';
import ZodiacDescriptionsDetail from '@/components/Astrology/ZodiacDescriptions/ZodiacDescriptionsDetail/ZodiacDescriptionsDetail.vue';

@Component({
    name: 'ZodiacDescriptionsList',
    components: {
        DefaultTools,
        Loader,
        Pagination,
        ZodiacDescriptionsCreate,
        ZodiacDescriptionsDetail,
        TableEmpty,
        Multiselect,
        CardHeader,
        cSwitch
    }
})
export default class ZodiacDescriptionsList extends Vue {

    // Begin Vuex Logic for ZodiacDescriptions
    @Action private zodiacDescriptionsList!: (query: IPayloadQuery) => number;
    @Getter private getZodiacDescriptions!: () => IZodiacDescriptions[];
    // End Vuex Logic for ZodiacDescriptions

    // Begin Vuex Logic for Zodiac Signs
    @Action private zodiacSignsList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private getZodiacSigns!: () => IZodiacSigns[];
    // End Vuex Logic for Zodiac Signs

    private loading: boolean = true;

    private createZodiacDescriptionsForm: boolean = false;
    private detailZodiacDescriptionsForm: boolean = false;

    private zodiacDescriptions: IZodiacDescriptions[] = [];
    private zodiacSigns: IMultiSelectItem[] = [];

    private selectedZodiacDescriptions: IZodiacDescriptions | null = null;

    private filtered: any = null;

    private selectedTableColumns: IMultiSelectItem[] = [];

    // Begin Config for Pagination
    private totalZodiacDescriptions: number | void | null = null;
    private currentPage: number = 1;
    private selectedLimit: string = '100';
    // End Config for Pagination


    // Begin Config for Table
    private showModalDetail(ZodiacDescriptions: IZodiacDescriptions) {
        this.detailZodiacDescriptionsForm = !this.detailZodiacDescriptionsForm;
        this.selectedZodiacDescriptions = cloneObject(ZodiacDescriptions);
    }

    private hideModalDetail() {
        this.detailZodiacDescriptionsForm = false;
        this.selectedZodiacDescriptions = null;
    }

    private showModalCreate() {
        this.createZodiacDescriptionsForm = !this.createZodiacDescriptionsForm
    }

    private hideModalCreate() {
        this.createZodiacDescriptionsForm = false;
    }

    private filterData(query: string) { // Method for search tool
        this.filtered = searchByTable(query, this.zodiacDescriptions)
    }

    private get tableItems(): IZodiacDescriptions[] {
        return this.filtered ? this.filtered : this.zodiacDescriptions;
    }

    private get tableColumns(): ITableCol[] | undefined {
        if (this.tableItems) {
            return getTableColumns(this.zodiacDescriptions, true, ['id']);
        }
    };

    private get fields() {
        if (this.tableColumns) {
            const columnsIds = this.selectedTableColumns.map((el: IMultiSelectItem) => el.value);
            return this.tableColumns.filter((el: any) => columnsIds.includes(el.key));
        } else {
            return this.tableColumns;
        }
    }

    // End Config for Table

    // Begin Query Logic
    private get payloadQuery(): IPayloadQuery {
        return {
            ...getParamQuery(this.$route),
            metaData: true
        }
    }

    private queryValue() {
        if (this.payloadQuery.query && this.payloadQuery.query.length > 0) {
            this.readQueryValue();
        } else {
            this.writeQueryValue();
            this.readQueryValue();
        }
    }

    private readQueryValue() {
        const queryData = getQueryData(this.$route.query);

        queryData.map(({key, value}) => {
            switch (key) {
                case 'page': {
                    this.currentPage = value;
                    break;
                }
                case 'limit': {
                    this.selectedLimit = value;
                    break;
                }
                default:
                    break;
            }
        });
    }

    @Watch('selectedLimit')
    @Watch('currentPage')
    private writeQueryValue() {
        const queryData: any = {
            page: this.currentPage,
            limit: this.selectedLimit
        };

        this.$router.replace({
            name: this.$route.name || undefined,
            query: cleanData(queryData)
        });
    }

    // End Query Logic

    @Watch('selectedLimit')
    @Watch('currentPage')
    private async getZodiacDescriptionsList() {
        try {
            this.loading = true;
            this.queryValue();

            this.totalZodiacDescriptions = await this.zodiacDescriptionsList(this.payloadQuery);
            this.zodiacDescriptions = this.getZodiacDescriptions();

            this.checkRelation();

            this.loading = false;

        } catch (error) {
            throw new Error(error);
        }
    }

    private checkRelation() {
        this.zodiacDescriptions.map((item: any) => {
            if (this.zodiacSigns) {
                this.zodiacSigns.forEach((zodiacSign: IMultiSelectItem) => {
                    if (item.zodiacSign === zodiacSign.value) {
                        item.zodiacSign = zodiacSign
                    }
                });
            }
        });
    }

    private async getRelations() {
        this.zodiacSigns = transformMultiSelect(this.getZodiacSigns(), 'title', 'id');
        if (isEmpty(this.getZodiacSigns())) {
            await this.zodiacSignsList({query: '?pagination=false'});
            this.zodiacSigns = transformMultiSelect(this.getZodiacSigns(), 'title', 'id');
        }
    }

    private async mounted() {
        try {
            await this.getRelations();
            await this.getZodiacDescriptionsList();
        } catch (error) {
            throw new Error(error);
        }
    }
}
